import React from 'react'
import { useAuthorizationContext } from '../../contexts/AuthorizationContext'
import { NotAuthorized } from '../pages/NotAuthorized/NotAuthorized'

interface RequireAuthorizedRouteProps {
  children: React.ReactNode
}

/**
 * Route-level gate: renders `children` for any signed-in user who has at least one allowed
 * ops-hub role, and <NotAuthorized /> for users who conclusively have none. Unlike
 * RequireRole/RequireWriteAccess (which gate individual pieces of UI), this is meant to wrap an
 * entire route's element in Router.tsx.
 *
 * Renders `children` optimistically while roles are still loading (`!rolesLoaded`), matching
 * App.tsx's app-shell gating, so this never flashes NotAuthorized before roles resolve.
 *
 * IMPORTANT: this is UX-only gating -- the backend (`POST /api/v1/operations`) independently
 * authenticates, authorizes, and audits every operation regardless of what this hides or shows.
 */
export const RequireAuthorizedRoute: React.FC<RequireAuthorizedRouteProps> = ({ children }) => {
  const { roles, rolesLoaded } = useAuthorizationContext()

  if (rolesLoaded && roles.length === 0) {
    return <NotAuthorized />
  }

  return <>{children}</>
}
